/** Zona horaria de la iglesia: Ciudad Juárez, Chihuahua. */
export const JUAREZ_TZ = (() => {
  for (const candidate of ['America/Ciudad_Juarez', 'America/Denver']) {
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: candidate });
      return candidate;
    } catch {
      // probar la siguiente
    }
  }
  return undefined;
})();

/** "Ahora" expresado en la hora de Ciudad Juárez (wall-clock). */
export function juarezNow(): Date {
  if (!JUAREZ_TZ) return new Date();

  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: JUAREZ_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());

  const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return new Date(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour') % 24,
    get('minute'),
    get('second'),
  );
}

function capitalize(value: string) {
  if (!value) return value;
  return value.charAt(0).toLocaleUpperCase('es-MX') + value.slice(1);
}

const juarezOptions = JUAREZ_TZ ? { timeZone: JUAREZ_TZ } : {};

export function formatJuarezDate(date: Date) {
  return capitalize(
    new Intl.DateTimeFormat('es-MX', {
      ...juarezOptions,
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }).format(date),
  );
}

export function formatJuarezTime(date: Date) {
  return new Intl.DateTimeFormat('es-MX', {
    ...juarezOptions,
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h12',
  }).format(date);
}

export function formatJuarezDay(date: Date) {
  return new Intl.DateTimeFormat('es-MX', {
    ...juarezOptions,
    day: 'numeric',
  }).format(date);
}

export function formatJuarezMonthShort(date: Date) {
  return capitalize(
    new Intl.DateTimeFormat('es-MX', {
      ...juarezOptions,
      month: 'short',
    }).format(date),
  ).replace('.', '');
}

export function formatJuarezWeekday(date: Date) {
  return capitalize(
    new Intl.DateTimeFormat('es-MX', {
      ...juarezOptions,
      weekday: 'long',
    }).format(date),
  );
}

/** Interpreta un reloj de pared de Ciudad Juárez como instante real (UTC). */
export function juarezWallClockToDate(
  year: number,
  month: number,
  day: number,
  hour = 0,
  minute = 0,
  second = 0,
) {
  if (!JUAREZ_TZ) {
    return new Date(year, month - 1, day, hour, minute, second);
  }

  let utc = Date.UTC(year, month - 1, day, hour, minute, second);
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: JUAREZ_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });

  const shownAsUtc = (ms: number) => {
    const parts = formatter.formatToParts(new Date(ms));
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);
    return Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  };

  const target = Date.UTC(year, month - 1, day, hour, minute, second);
  for (let index = 0; index < 4; index += 1) {
    utc += target - shownAsUtc(utc);
  }

  return new Date(utc);
}
